import React, { useState } from 'react';
import { MealItem } from '../api';

interface DishActionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  dishItems: MealItem[];
  onRename: (newName: string) => void;
  onDelete: () => void;
}

const DishActionsModal: React.FC<DishActionsModalProps> = ({
  isOpen,
  onClose,
  dishItems,
  onRename,
  onDelete
}) => {
  const [newName, setNewName] = useState(dishItems[0]?.dish_name || '');
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  if (!isOpen) return null;

  const handleRename = () => {
    if (newName.trim() && newName.trim() !== dishItems[0]?.dish_name) {
      onRename(newName.trim());
      onClose();
    }
  };

  const handleDelete = () => {
    if (isConfirmingDelete) {
      onDelete();
      onClose();
    } else {
      setIsConfirmingDelete(true);
    }
  };

  const resetDeleteConfirmation = () => {
    setIsConfirmingDelete(false);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
          <div>
            <div className="mt-3 text-center sm:mt-0 sm:text-left">
              <h3 className="text-base font-semibold leading-6 text-gray-900 mb-4">
                Manage Dish
              </h3>
              
              <div className="space-y-4">
                {/* Current dish info */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600 mb-1">Current name:</p>
                  <p className="font-medium">{dishItems[0]?.dish_name || 'Unnamed Dish'}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {dishItems.length} item{dishItems.length !== 1 ? 's' : ''}
                  </p>
                </div>

                {/* Rename section */}
                <div>
                  <label htmlFor="dishName" className="block text-sm font-medium text-gray-700 mb-1">
                    Rename dish:
                  </label>
                  <input
                    type="text"
                    id="dishName"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter new dish name"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 space-y-3">
            {/* Rename button */}
            <button
              type="button"
              onClick={handleRename}
              disabled={!newName.trim() || newName.trim() === dishItems[0]?.dish_name}
              className="w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Rename Dish
            </button>
            
            {/* Delete button */}
            <button
              type="button"
              onClick={handleDelete}
              onMouseLeave={resetDeleteConfirmation}
              className={`w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                isConfirmingDelete
                  ? 'bg-red-600 text-white hover:bg-red-500 focus-visible:outline-red-600'
                  : 'bg-red-50 text-red-600 hover:bg-red-100 focus-visible:outline-red-600'
              }`}
            >
              {isConfirmingDelete ? 'Confirm Delete' : 'Delete Entire Dish'}
            </button>
            
            {/* Cancel button */}
            <button
              type="button"
              onClick={onClose}
              className="w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DishActionsModal;